/** BC unit codes arrive unvalidated from JSON, so they may be null, padded or upper-case. */
const normalizeUnit = (unit: string | undefined): string => unit?.trim().toLowerCase() ?? "";

export const isDecimalUnit = (unit: string | undefined): boolean => {
  return normalizeUnit(unit) === "kg";
};

const WHOLE_NUMBER_UNITS = new Set(["st", "stk", "pcs"]);

/** Allowlisted, because rounding an unrecognised code (KGM, G, LTR, or none at all) would file a wrong weight. */
export const isWholeNumberUnit = (unit: string | undefined): boolean => {
  return WHOLE_NUMBER_UNITS.has(normalizeUnit(unit));
};

/** Snaps a quantity to the precision its unit allows: whole numbers for piece units, 3 decimals otherwise. */
export const normalizeQuantity = (value: number, unit: string | undefined): number => {
  return isWholeNumberUnit(unit) ? Math.round(value) : Math.round(value * 1000) / 1000;
};

export const formatQuantity = (value: number, unit: string | undefined): string => {
  if (isWholeNumberUnit(unit)) {
    return value.toLocaleString("de-DE", { maximumFractionDigits: 0 });
  }
  return isDecimalUnit(unit)
    ? value.toLocaleString("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : value.toLocaleString("de-DE", { maximumFractionDigits: 3 });
};

/**
 * Formats a signed quantity change with an explicit plus sign, so a history row reads as a
 * change ("+1.000", "-200") rather than as an absolute quantity.
 */
export const formatQuantityDelta = (value: number, unit: string | undefined): string => {
  const formatted = formatQuantity(value, unit);
  return value > 0 ? `+${formatted}` : formatted;
};
